/**
 * 90-Day Action Plan Generator
 *
 * Creates personalized, actionable career transition plans based on:
 * - Target career requirements
 * - User's current situation (segment)
 * - Skills gap analysis
 * - Market conditions
 */

import { CareerPath } from './careerPaths';

export interface ActionItem {
  id: string;
  title: string;
  description: string;
  type: 'learning' | 'networking' | 'portfolio' | 'application' | 'certification' | 'research' | 'skill-building';
  priority: 'critical' | 'high' | 'medium';
  estimatedHours: number;
  resources?: ActionResource[];
  checkpoints?: string[];
}

export interface ActionResource {
  name: string;
  url?: string;
  type: 'course' | 'book' | 'website' | 'community' | 'tool' | 'certification';
  cost: 'free' | 'paid' | 'freemium';
  note?: string;
}

export interface WeekPlan {
  week: number;
  theme: string;
  focus: string;
  actions: ActionItem[];
  milestone: string;
}

export interface MonthPlan {
  month: number;
  theme: string;
  goal: string;
  weeks: WeekPlan[];
  keyMetrics: string[];
}

export interface ActionPlan {
  career: {
    id: string;
    title: string;
  };
  userSegment: string;
  generatedAt: Date;
  overview: {
    headline: string;
    summary: string;
    successProbability: 'high' | 'medium' | 'achievable';
    timeToFirstRole: string;
    investmentRequired: string;
  };
  months: MonthPlan[];
  quickWins: ActionItem[];
  criticalPath: string[];
  potentialBlockers: string[];
  successIndicators: string[];
}

// Career-specific action plan templates
const CAREER_PLAN_TEMPLATES: Record<string, Partial<ActionPlan>> = {
  // Tech careers
  web_developer: {
    overview: {
      headline: "From Zero to Junior Developer in 90 Days",
      summary: "A structured path to land your first web development role, focusing on portfolio projects and practical skills.",
      successProbability: 'high',
      timeToFirstRole: '3-6 months',
      investmentRequired: '$0-500 (mostly free resources)',
    },
    criticalPath: [
      "Complete HTML/CSS/JavaScript fundamentals",
      "Build 3 portfolio projects",
      "Create GitHub profile with clean code",
      "Deploy projects to live URLs",
      "Apply to 10+ entry-level positions",
    ],
    potentialBlockers: [
      "Tutorial hell - watching but not building",
      "Perfectionism - waiting to feel 'ready'",
      "Isolation - not connecting with dev community",
    ],
  },

  customer_success_specialist: {
    overview: {
      headline: "Transition to Customer Success in 90 Days",
      summary: "Leverage your people skills into a high-growth SaaS career with clear advancement paths.",
      successProbability: 'high',
      timeToFirstRole: '1-3 months',
      investmentRequired: '$0-200',
    },
    criticalPath: [
      "Learn SaaS metrics and terminology",
      "Get certified in a CRM tool (HubSpot/Salesforce)",
      "Build customer success portfolio with case studies",
      "Network with CS professionals on LinkedIn",
      "Apply to startups and scale-ups",
    ],
    potentialBlockers: [
      "Lack of SaaS experience",
      "No technical background",
      "Unfamiliar with B2B environment",
    ],
  },

  electrician: {
    overview: {
      headline: "Start Your Electrician Career in 90 Days",
      summary: "Begin your path to a licensed electrician through apprenticeship or trade school.",
      successProbability: 'high',
      timeToFirstRole: '1-2 months (apprentice)',
      investmentRequired: '$0-5,000 (often employer-paid)',
    },
    criticalPath: [
      "Research state licensing requirements",
      "Apply to union (IBEW) or non-union apprenticeships",
      "Complete pre-apprenticeship math assessment",
      "Get OSHA 10 safety certification",
      "Secure apprenticeship placement",
    ],
    potentialBlockers: [
      "Math skills need refreshing",
      "Physical fitness requirements",
      "Apprenticeship waitlists",
    ],
  },

  personal_trainer: {
    overview: {
      headline: "Become a Certified Personal Trainer in 90 Days",
      summary: "Turn your fitness passion into a career with certification and client acquisition strategies.",
      successProbability: 'high',
      timeToFirstRole: '2-3 months',
      investmentRequired: '$400-1,000 (certification)',
    },
    criticalPath: [
      "Choose and enroll in certification program (NASM, ACE, or ISSA)",
      "Study for and pass certification exam",
      "Get CPR/AED certified",
      "Build social media presence showcasing fitness journey",
      "Apply to gyms or start independent training",
    ],
    potentialBlockers: [
      "Certification cost",
      "Building initial client base",
      "Insurance and liability concerns",
    ],
  },

  graphic_designer: {
    overview: {
      headline: "Launch Your Design Career in 90 Days",
      summary: "Build a compelling portfolio and break into the design industry without a degree.",
      successProbability: 'medium',
      timeToFirstRole: '3-6 months',
      investmentRequired: '$0-300 (software subscriptions)',
    },
    criticalPath: [
      "Master design fundamentals (typography, color, layout)",
      "Learn industry-standard tools (Figma, Adobe CC)",
      "Complete 5+ portfolio pieces across different project types",
      "Build online portfolio website",
      "Start freelancing to build client testimonials",
    ],
    potentialBlockers: [
      "No formal design education",
      "Competitive job market",
      "Portfolio quality vs. quantity balance",
    ],
  },

  project_manager: {
    overview: {
      headline: "Transition to Project Management in 90 Days",
      summary: "Leverage your organizational skills and gain PM credentials for a leadership role.",
      successProbability: 'high',
      timeToFirstRole: '2-4 months',
      investmentRequired: '$200-500 (certifications)',
    },
    criticalPath: [
      "Complete Google Project Management Certificate",
      "Learn Agile/Scrum methodology",
      "Get Scrum Master or CAPM certification",
      "Document past project experience in PM terms",
      "Target industries matching your background",
    ],
    potentialBlockers: [
      "Lack of official PM title experience",
      "PMP requires 3+ years experience",
      "Industry-specific knowledge gaps",
    ],
  },

  medical_assistant: {
    overview: {
      headline: "Become a Medical Assistant in 90 Days",
      summary: "Fast-track into healthcare with certification and clinical training.",
      successProbability: 'high',
      timeToFirstRole: '3-6 months',
      investmentRequired: '$1,000-3,000 (accelerated program)',
    },
    criticalPath: [
      "Enroll in accelerated MA certification program",
      "Complete clinical externship hours",
      "Pass CMA or RMA certification exam",
      "Get CPR/BLS certified",
      "Apply to clinics, hospitals, and specialty practices",
    ],
    potentialBlockers: [
      "Program scheduling conflicts",
      "Externship availability",
      "Background check requirements",
    ],
  },
};

// Generic week templates by phase
const WEEK_TEMPLATES = {
  week1: {
    theme: "Foundation & Research",
    focus: "Understand the landscape and set up for success",
  },
  week2: {
    theme: "Skill Assessment & Planning",
    focus: "Identify gaps and create learning roadmap",
  },
  week3: {
    theme: "Active Learning Begins",
    focus: "Start building core skills",
  },
  week4: {
    theme: "First Milestone",
    focus: "Complete first tangible deliverable",
  },
  week5_6: {
    theme: "Deep Skill Building",
    focus: "Intensive learning and practice",
  },
  week7_8: {
    theme: "Portfolio Development",
    focus: "Create work samples and case studies",
  },
  week9_10: {
    theme: "Network & Visibility",
    focus: "Build connections and online presence",
  },
  week11_12: {
    theme: "Application Sprint",
    focus: "Active job search and interviews",
  },
};

// Common resources by type
const COMMON_RESOURCES: Record<string, ActionResource[]> = {
  learning_platforms: [
    { name: "Coursera", url: "https://coursera.org", type: "course", cost: "freemium" },
    { name: "LinkedIn Learning", url: "https://linkedin.com/learning", type: "course", cost: "paid" },
    { name: "Udemy", url: "https://udemy.com", type: "course", cost: "paid", note: "Wait for sales - courses drop to $10-15" },
    { name: "YouTube", url: "https://youtube.com", type: "course", cost: "free" },
    { name: "freeCodeCamp", url: "https://freecodecamp.org", type: "course", cost: "free" },
  ],
  networking: [
    { name: "LinkedIn", url: "https://linkedin.com", type: "community", cost: "freemium" },
    { name: "Meetup", url: "https://meetup.com", type: "community", cost: "free" },
    { name: "Discord Communities", type: "community", cost: "free" },
  ],
  job_boards: [
    { name: "Indeed", url: "https://indeed.com", type: "website", cost: "free" },
    { name: "LinkedIn Jobs", url: "https://linkedin.com/jobs", type: "website", cost: "free" },
    { name: "Glassdoor", url: "https://glassdoor.com", type: "website", cost: "free" },
  ],
};

/**
 * Generate a complete 90-day action plan for a career transition
 */
export function generateActionPlan(
  career: CareerPath,
  userSegment: string | null,
  userStrengths: string[] = []
): ActionPlan {
  const template = CAREER_PLAN_TEMPLATES[career.id] || {};
  const segment = userSegment || 'general';

  // Generate overview
  const overview = template.overview || generateGenericOverview(career);

  // Generate monthly plans
  const months = generateMonthlyPlans(career, segment, userStrengths);

  // Generate quick wins
  const quickWins = generateQuickWins(career, segment);

  // Generate critical path
  const criticalPath = template.criticalPath || generateCriticalPath(career);

  // Generate blockers
  const potentialBlockers = template.potentialBlockers || generateBlockers(career, segment);

  // Success indicators
  const successIndicators = generateSuccessIndicators(career);

  return {
    career: {
      id: career.id,
      title: career.title,
    },
    userSegment: segment,
    generatedAt: new Date(),
    overview,
    months,
    quickWins,
    criticalPath,
    potentialBlockers,
    successIndicators,
  };
}

function generateGenericOverview(career: CareerPath): ActionPlan['overview'] {
  const isEntryLevel = career.salary.entry_min < 40000;
  const probability = isEntryLevel ? 'high' : 'medium';

  return {
    headline: `Your Path to Becoming a ${career.title}`,
    summary: `A structured 90-day plan to break into ${career.category} and land your first ${career.title} role.`,
    successProbability: probability,
    timeToFirstRole: isEntryLevel ? '2-4 months' : '3-6 months',
    investmentRequired: '$0-500',
  };
}

function generateMonthlyPlans(
  career: CareerPath,
  segment: string,
  strengths: string[]
): MonthPlan[] {
  return [
    generateMonth1(career, segment),
    generateMonth2(career, segment),
    generateMonth3(career, segment),
  ];
}

function generateMonth1(career: CareerPath, segment: string): MonthPlan {
  const isVeteran = segment === 'veteran' || segment === 'military';
  const isCareerChanger = segment === 'career-changer';
  const isReturning = segment === 'returning';

  return {
    month: 1,
    theme: "Foundation & First Steps",
    goal: "Understand the career landscape and begin skill development",
    keyMetrics: [
      "Complete career research",
      "Identify 3 key skills to develop",
      "Set up professional online presence",
      "Connect with 5 people in the field",
    ],
    weeks: [
      {
        week: 1,
        theme: WEEK_TEMPLATES.week1.theme,
        focus: WEEK_TEMPLATES.week1.focus,
        milestone: "Career landscape understood",
        actions: [
          {
            id: "research-1",
            title: "Research the career in depth",
            description: `Understand what ${career.title}s actually do day-to-day, required skills, and typical career paths.`,
            type: "research",
            priority: "critical",
            estimatedHours: 4,
            resources: [
              { name: "O*NET OnLine", url: "https://onetonline.org", type: "website", cost: "free" },
              { name: "Bureau of Labor Statistics", url: "https://bls.gov/ooh", type: "website", cost: "free" },
              { name: "Reddit career communities", url: "https://reddit.com", type: "community", cost: "free" },
            ],
            checkpoints: [
              "List 5 daily activities of this role",
              "Identify salary ranges in your area",
              "Find 3 common career advancement paths",
            ],
          },
          {
            id: "network-1",
            title: "Find and follow industry voices",
            description: "Identify thought leaders, companies, and communities in your target field.",
            type: "networking",
            priority: "high",
            estimatedHours: 2,
            resources: COMMON_RESOURCES.networking,
          },
          {
            id: "profile-1",
            title: "Optimize your LinkedIn profile",
            description: isCareerChanger
              ? "Reframe your experience to highlight transferable skills for this new career."
              : "Create or update your LinkedIn with your career transition goal.",
            type: "portfolio",
            priority: "high",
            estimatedHours: 3,
          },
        ],
      },
      {
        week: 2,
        theme: WEEK_TEMPLATES.week2.theme,
        focus: WEEK_TEMPLATES.week2.focus,
        milestone: "Learning plan created",
        actions: [
          {
            id: "skills-gap",
            title: "Complete skills gap analysis",
            description: "Compare your current skills against job requirements and identify gaps.",
            type: "research",
            priority: "critical",
            estimatedHours: 3,
            checkpoints: [
              "List all required skills from 5 job postings",
              "Rate yourself 1-5 on each skill",
              "Prioritize top 3 skills to develop",
            ],
          },
          {
            id: "learning-plan",
            title: "Select learning resources",
            description: "Choose courses, books, or programs to develop your priority skills.",
            type: "learning",
            priority: "high",
            estimatedHours: 2,
            resources: COMMON_RESOURCES.learning_platforms,
          },
          {
            id: "network-2",
            title: "Send 5 connection requests",
            description: `Connect with ${career.title}s and ask about their career path. Most people love sharing advice.`,
            type: "networking",
            priority: "medium",
            estimatedHours: 1,
          },
        ],
      },
      {
        week: 3,
        theme: WEEK_TEMPLATES.week3.theme,
        focus: WEEK_TEMPLATES.week3.focus,
        milestone: "Active learning underway",
        actions: [
          {
            id: "course-1",
            title: "Begin primary learning course",
            description: "Start your main skill-building course or program. Aim for 1-2 hours daily.",
            type: "learning",
            priority: "critical",
            estimatedHours: 10,
          },
          {
            id: "practice-1",
            title: "Apply learning immediately",
            description: "Don't just watch tutorials - practice what you learn the same day.",
            type: "skill-building",
            priority: "high",
            estimatedHours: 5,
          },
          {
            id: isVeteran ? "veteran-translate" : "experience-frame",
            title: isVeteran
              ? "Translate military experience"
              : "Document transferable experience",
            description: isVeteran
              ? "Use military-to-civilian skill translators to reframe your experience."
              : "Identify past experiences that demonstrate relevant skills.",
            type: "portfolio",
            priority: "high",
            estimatedHours: 3,
            resources: isVeteran ? [
              { name: "Military Skills Translator", url: "https://military.com/skills-translator", type: "tool", cost: "free" },
              { name: "O*NET Military Crosswalk", url: "https://onetonline.org/crosswalk", type: "tool", cost: "free" },
            ] : [],
          },
        ],
      },
      {
        week: 4,
        theme: WEEK_TEMPLATES.week4.theme,
        focus: WEEK_TEMPLATES.week4.focus,
        milestone: "First project completed",
        actions: [
          {
            id: "project-1",
            title: "Complete first mini-project",
            description: "Apply your new skills to create something tangible, even if small.",
            type: "portfolio",
            priority: "critical",
            estimatedHours: 8,
          },
          {
            id: "feedback-1",
            title: "Get feedback on your work",
            description: "Share your project with your new network connections and ask for honest feedback.",
            type: "networking",
            priority: "high",
            estimatedHours: 2,
          },
          {
            id: "month1-review",
            title: "Month 1 progress review",
            description: "Assess what's working, what isn't, and adjust your plan for Month 2.",
            type: "research",
            priority: "medium",
            estimatedHours: 1,
          },
        ],
      },
    ],
  };
}

function generateMonth2(career: CareerPath, segment: string): MonthPlan {
  return {
    month: 2,
    theme: "Deep Skill Building & Proof of Work",
    goal: "Develop demonstrable skills and build portfolio evidence",
    keyMetrics: [
      "Complete 50%+ of primary course",
      "Build 2-3 portfolio pieces",
      "Attend 2 industry events/meetups",
      "Have 3 informational interviews",
    ],
    weeks: [
      {
        week: 5,
        theme: "Intensive Skill Building",
        focus: "Deep dive into core competencies",
        milestone: "Core skills strengthened",
        actions: [
          {
            id: "course-continue",
            title: "Continue primary learning (10+ hrs/week)",
            description: "Maintain momentum on your main learning track. Consistency beats intensity.",
            type: "learning",
            priority: "critical",
            estimatedHours: 12,
          },
          {
            id: "project-2",
            title: "Start second portfolio project",
            description: "Begin a more ambitious project that showcases multiple skills.",
            type: "portfolio",
            priority: "high",
            estimatedHours: 6,
          },
        ],
      },
      {
        week: 6,
        theme: "Community Engagement",
        focus: "Build relationships and learn from practitioners",
        milestone: "Active in community",
        actions: [
          {
            id: "event-1",
            title: "Attend industry meetup or event",
            description: "Join a local or virtual event. Introduce yourself and exchange contact info with 3+ people.",
            type: "networking",
            priority: "high",
            estimatedHours: 3,
            resources: [
              { name: "Meetup", url: "https://meetup.com", type: "community", cost: "free" },
              { name: "Eventbrite", url: "https://eventbrite.com", type: "community", cost: "free" },
            ],
          },
          {
            id: "info-interview-1",
            title: "Conduct first informational interview",
            description: `Reach out to a ${career.title} and ask for 20 minutes to learn about their experience.`,
            type: "networking",
            priority: "high",
            estimatedHours: 1,
            checkpoints: [
              "How did you get started in this field?",
              "What do you wish you knew before starting?",
              "What skills are most important for success?",
              "Any advice for someone breaking in?",
            ],
          },
        ],
      },
      {
        week: 7,
        theme: "Portfolio Polish",
        focus: "Refine and document your work",
        milestone: "Portfolio taking shape",
        actions: [
          {
            id: "project-2-complete",
            title: "Complete second portfolio project",
            description: "Finish and document your project with clear explanations of your process.",
            type: "portfolio",
            priority: "critical",
            estimatedHours: 8,
          },
          {
            id: "case-study-1",
            title: "Write first case study",
            description: "Document one project as a case study: problem, process, solution, results.",
            type: "portfolio",
            priority: "high",
            estimatedHours: 3,
          },
        ],
      },
      {
        week: 8,
        theme: "Certification Prep",
        focus: "Add credentials that boost your credibility",
        milestone: "Certification in progress",
        actions: [
          {
            id: "cert-research",
            title: "Research relevant certifications",
            description: `Identify which certifications are most valued for ${career.title} roles.`,
            type: "research",
            priority: "high",
            estimatedHours: 2,
          },
          {
            id: "cert-enroll",
            title: "Enroll in certification program",
            description: "Start studying for or enroll in a certification that will strengthen your candidacy.",
            type: "certification",
            priority: "high",
            estimatedHours: 4,
          },
          {
            id: "month2-review",
            title: "Month 2 progress review",
            description: "Assess portfolio strength and prepare for Month 3 application push.",
            type: "research",
            priority: "medium",
            estimatedHours: 1,
          },
        ],
      },
    ],
  };
}

function generateMonth3(career: CareerPath, segment: string): MonthPlan {
  return {
    month: 3,
    theme: "Application & Interview Sprint",
    goal: "Actively apply and interview for positions",
    keyMetrics: [
      "Apply to 20+ positions",
      "Complete portfolio website",
      "Get 3+ interviews",
      "Finish certification (if applicable)",
    ],
    weeks: [
      {
        week: 9,
        theme: "Application Prep",
        focus: "Prepare all application materials",
        milestone: "Application package ready",
        actions: [
          {
            id: "resume-final",
            title: "Finalize tailored resume",
            description: `Create a resume specifically targeting ${career.title} positions with relevant keywords.`,
            type: "application",
            priority: "critical",
            estimatedHours: 4,
          },
          {
            id: "portfolio-site",
            title: "Launch portfolio website",
            description: "Create a simple site showcasing your projects, skills, and story.",
            type: "portfolio",
            priority: "high",
            estimatedHours: 6,
          },
          {
            id: "cover-letter-template",
            title: "Create cover letter template",
            description: "Write a compelling template you can customize for each application.",
            type: "application",
            priority: "high",
            estimatedHours: 2,
          },
        ],
      },
      {
        week: 10,
        theme: "Active Applications",
        focus: "Apply strategically to target companies",
        milestone: "First applications sent",
        actions: [
          {
            id: "target-companies",
            title: "Create target company list",
            description: "Identify 20+ companies hiring for your target role. Prioritize by culture fit and growth opportunity.",
            type: "research",
            priority: "critical",
            estimatedHours: 3,
          },
          {
            id: "apply-batch-1",
            title: "Submit first 10 applications",
            description: "Apply to your first batch of positions. Customize each cover letter.",
            type: "application",
            priority: "critical",
            estimatedHours: 8,
            resources: COMMON_RESOURCES.job_boards,
          },
          {
            id: "referral-outreach",
            title: "Ask network for referrals",
            description: "Reach out to your connections at target companies for internal referrals.",
            type: "networking",
            priority: "high",
            estimatedHours: 2,
          },
        ],
      },
      {
        week: 11,
        theme: "Interview Preparation",
        focus: "Prepare for and conduct interviews",
        milestone: "Interview ready",
        actions: [
          {
            id: "interview-prep",
            title: "Practice common interview questions",
            description: `Research and practice the most common ${career.title} interview questions.`,
            type: "application",
            priority: "critical",
            estimatedHours: 4,
            checkpoints: [
              "Prepare 'Tell me about yourself' (2 min)",
              "Practice STAR method for behavioral questions",
              "Prepare 5 questions to ask interviewers",
              "Research company before each interview",
            ],
          },
          {
            id: "apply-batch-2",
            title: "Submit next 10 applications",
            description: "Continue applying while preparing for interviews.",
            type: "application",
            priority: "high",
            estimatedHours: 6,
          },
          {
            id: "mock-interview",
            title: "Conduct mock interview",
            description: "Practice with a friend, mentor, or online service.",
            type: "application",
            priority: "high",
            estimatedHours: 2,
          },
        ],
      },
      {
        week: 12,
        theme: "Closing Strong",
        focus: "Follow through and negotiate",
        milestone: "Offers in progress",
        actions: [
          {
            id: "follow-up",
            title: "Follow up on all applications",
            description: "Send polite follow-up emails to companies where you haven't heard back.",
            type: "application",
            priority: "high",
            estimatedHours: 2,
          },
          {
            id: "negotiation-prep",
            title: "Prepare for salary negotiation",
            description: "Research salary ranges and prepare your negotiation strategy.",
            type: "research",
            priority: "high",
            estimatedHours: 2,
          },
          {
            id: "90-day-review",
            title: "Complete 90-day review",
            description: "Document your progress, learnings, and next steps whether or not you've landed a role yet.",
            type: "research",
            priority: "medium",
            estimatedHours: 1,
          },
          {
            id: "continue-momentum",
            title: "Plan your next 30 days",
            description: "If you haven't landed a role yet, that's normal! Plan your continued job search.",
            type: "research",
            priority: "medium",
            estimatedHours: 1,
          },
        ],
      },
    ],
  };
}

function generateQuickWins(career: CareerPath, segment: string): ActionItem[] {
  const baseQuickWins: ActionItem[] = [
    {
      id: "qw-linkedin",
      title: "Update LinkedIn headline today",
      description: `Change to: "Aspiring ${career.title} | [Your Current Skills] | Open to Opportunities"`,
      type: "portfolio",
      priority: "high",
      estimatedHours: 0.25,
    },
    {
      id: "qw-follow",
      title: "Follow 10 industry leaders",
      description: "Find and follow thought leaders in your target field on LinkedIn and Twitter.",
      type: "networking",
      priority: "medium",
      estimatedHours: 0.5,
    },
    {
      id: "qw-community",
      title: "Join 2 online communities",
      description: "Find Discord servers, Slack groups, or subreddits for your target career.",
      type: "networking",
      priority: "medium",
      estimatedHours: 0.5,
    },
    {
      id: "qw-bookmark",
      title: "Bookmark 5 job postings",
      description: "Save job postings to understand requirements, even if you won't apply yet.",
      type: "research",
      priority: "high",
      estimatedHours: 0.5,
    },
  ];

  return baseQuickWins;
}

function generateCriticalPath(career: CareerPath): string[] {
  return [
    `Develop core ${career.category} skills`,
    "Build portfolio with 3+ projects",
    "Get relevant certification(s)",
    "Network with industry professionals",
    "Apply strategically to target companies",
  ];
}

function generateBlockers(career: CareerPath, segment: string): string[] {
  const commonBlockers = [
    "Analysis paralysis - overthinking instead of doing",
    "Impostor syndrome - feeling not 'ready enough'",
    "Inconsistency - starting strong but losing momentum",
    "Isolation - not building a support network",
  ];

  if (segment === 'returning') {
    commonBlockers.push("Confidence gap from time away from work");
    commonBlockers.push("Concerns about explaining career gap");
  }

  if (segment === 'career-changer') {
    commonBlockers.push("Undervaluing transferable skills");
    commonBlockers.push("Comparing yourself to career-long professionals");
  }

  return commonBlockers;
}

function generateSuccessIndicators(career: CareerPath): string[] {
  return [
    "Completing daily/weekly learning goals consistently",
    "Growing professional network (10+ new connections/month)",
    "Receiving positive feedback on portfolio work",
    "Getting callbacks or interview requests",
    "Feeling more confident discussing the career",
    "Understanding industry terminology and trends",
  ];
}

/**
 * Get estimated time to complete the action plan
 */
export function getEstimatedTotalHours(plan: ActionPlan): number {
  let total = 0;
  for (const month of plan.months) {
    for (const week of month.weeks) {
      for (const action of week.actions) {
        total += action.estimatedHours;
      }
    }
  }
  return total;
}

/**
 * Get completion percentage based on checked items
 */
export function calculateProgress(plan: ActionPlan, completedActionIds: string[]): number {
  let totalActions = 0;
  for (const month of plan.months) {
    for (const week of month.weeks) {
      totalActions += week.actions.length;
    }
  }
  return totalActions > 0 ? (completedActionIds.length / totalActions) * 100 : 0;
}
